import { Alert, Box, Container, Paper, Typography, IconButton, Stack, Button, useTheme, useMediaQuery } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import i18next from '../../lang';
import { rootStore } from '../../stores/RootStore';
import ConfirmDialog from "../../components/ConfirmDialog";
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import CampaignIcon from '@mui/icons-material/Campaign';
import ReorderIcon from '@mui/icons-material/Reorder';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

interface Announcement {
  id: number;
  userId: string;
  title: string;
  content: string;
  isImportant: number;
  createdAt: string;
  updatedAt: string;
}

const StoreMain = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authStore } = rootStore;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [tempOrder, setTempOrder] = useState<any>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [pendingCount, setPendingCount] = useState(0);

  const slides = [
    { image: 'https://kokugoteki.jp/wp-content/themes/kokugoteki/assets/images/index/topImg1_PC.jpg' },
    { image: 'https://kokugoteki.jp/wp-content/themes/kokugoteki/assets/images/index/topImg2_PC.jpg' },
    { image: 'https://kokugoteki.jp/wp-content/themes/kokugoteki/assets/images/index/topImg3_PC.jpg' },
  ];

  useEffect(() => {
    let timer: NodeJS.Timeout;
    const startTimer = () => {
      timer = setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        startTimer();
      }, 6000);
    };
    startTimer();
    return () => clearTimeout(timer);
  }, [slides.length]);

  useEffect(() => {
    const checkTempOrder = async () => {
      let tempOrderData = location.state?.tempOrder;
      if (!tempOrderData && authStore.user && authStore.user.level !== 'ADMIN') {
        try {
          const response = await axios.get('/api/v1/orders/temp');
          if (response.data) {
            tempOrderData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
          }
        } catch { /* no temp order */ }
      } else if (typeof tempOrderData === 'string') {
        try { tempOrderData = JSON.parse(tempOrderData); } catch { /* already object */ }
      }
      if (tempOrderData && typeof tempOrderData === 'object' && Object.keys(tempOrderData).length > 0) {
        setTempOrder(tempOrderData);
        window.history.replaceState({}, document.title);
      }
    };
    checkTempOrder();
  }, [location.state, authStore.user]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/admin/announcements');

      setAnnouncements(response.data.announcements);
    } catch (error: any) {
      console.error('Failed to fetch announcements:', error);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleDeleteTempOrder = async () => {
    try {
      await axios.delete(`/api/v1/orders/temp`);
    } catch (error) {
      console.error('Failed to delete temp order:', error);
    }
    setTempOrder(null);
  }

  const handleGoToTempOrder = () => {
    navigate('/purchase/order-input', { state: { tempOrder } });
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'Z');
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const user = toJS(authStore.user);

  useEffect(() => {
    if (user?.level === 'ADMIN') {
      axios.get('/api/v1/users/pending-count')
        .then(res => setPendingCount(res.data.pendingCount))
        .catch(() => {});
    }
  }, [user?.level]);


  return (
    <Box>
      {/* Navigation Bar */}

      {/* Site Description & Navigation Buttons */}
      <Box sx={{ py: { xs: 3, md: 5 } }}>
        <Container maxWidth="lg">
          {authStore.isAuthenticated && user?.level !== 'ADMIN' && (
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: '12px' }}>
              {user?.username && (
                <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
                  {i18next.t('top.welcome')} {user.username} {i18next.t('top.honorific')}
                </Typography>
              )}
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                {i18next.t('top.site_description')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {i18next.t('top.site_notice')}
              </Typography>
            </Paper>
          )}

          {user?.level === 'ADMIN' && pendingCount > 0 && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ mb: 2, cursor: 'pointer', borderRadius: '12px' }}
              onClick={() => navigate('/admin/users?status=PENDING')}
            >
              {i18next.t('messenger.pending_users', { count: pendingCount })}
            </Alert>
          )}

          {user?.level !== 'ADMIN' && tempOrder && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ mb: 2, borderRadius: '12px' }}
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button color="inherit" size="small" onClick={handleDeleteTempOrder}>
                    {i18next.t('purchase.delete_temp_order')}
                  </Button>
                  <Button color="warning" size="small" variant="contained" onClick={handleGoToTempOrder}>
                    {i18next.t('purchase.go_to_temp_order')}
                  </Button>
                </Box>
              }
            >
              {i18next.t('purchase.temp_order_found_message')}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center', mb: 3 }}>
            {user?.level === 'ADMIN' ? (
              <>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ReceiptLongIcon />}
                  onClick={() => navigate('/invoices')}
                  sx={{
                    py: 2.5,
                    px: 5,
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    fontWeight: 'bold',
                    minWidth: { xs: '100%', sm: '220px' },
                    borderRadius: 2,
                  }}
                >
                  {i18next.t('nav.invoice')}
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ListAltIcon />}
                  onClick={() => navigate('/orders')}
                  sx={{
                    py: 2.5,
                    px: 5,
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    fontWeight: 'bold',
                    minWidth: { xs: '100%', sm: '220px' },
                    borderRadius: 2,
                    backgroundColor: '#1976d2',
                    '&:hover': { backgroundColor: '#1565c0' }
                  }}
                >
                  {i18next.t('nav.order_status')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<PeopleIcon />}
                  onClick={() => navigate('/admin/users')}
                  sx={{
                    py: 2.5,
                    px: 5,
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    fontWeight: 'bold',
                    minWidth: { xs: '100%', sm: '220px' },
                    borderRadius: 2,
                  }}
                >
                  {i18next.t('nav.user_management')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ShoppingCartIcon />}
                  onClick={() => {
                    if (authStore.user?.level === 'CLASSROOM_MANAGER') {
                      navigate('/purchase/delivery-datetime');
                    } else {
                      navigate('/purchase/delivery-address');
                    }
                  }}
                  sx={{
                    py: 2.5,
                    px: 5,
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    fontWeight: 'bold',
                    minWidth: { xs: '100%', sm: '220px' },
                    borderRadius: 2,
                  }}
                >
                  {i18next.t('nav.purchase')}
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ListAltIcon />}
                  onClick={() => navigate('/orders')}
                  sx={{
                    py: 2.5,
                    px: 5,
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    fontWeight: 'bold',
                    minWidth: { xs: '100%', sm: '220px' },
                    borderRadius: 2,
                    backgroundColor: '#1976d2',
                    '&:hover': { backgroundColor: '#1565c0' }
                  }}
                >
                  {i18next.t('nav.order_status')}
                </Button>
              </>
            )}
          </Box>
        </Container>
      </Box>

      {/* Hero Section — 未ログイン時のみ表示 */}
      {!authStore.isAuthenticated && <Box sx={{
        position: 'relative',
        bgcolor: 'transparent',
        color: 'text.primary',
        minHeight: { xs: '300px', md: '600px' },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        overflow: 'hidden',
        borderBottom: '1px solid rgba(0,0,0,0.05)'
      }}>
        {slides.map((slide, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: currentSlide === index ? 1 : 0,
              transition: 'opacity 2s ease-in-out',
              zIndex: currentSlide === index ? 0 : -1,
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                backgroundImage: `url(${slide.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                transform: currentSlide === index ? 'scale(1.1)' : 'scale(1.0)',
                transition: 'transform 7s ease-out',
                maskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
              }}
            />
          </Box>
        ))}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            padding: { xs: '1rem', md: '2rem' },
            borderRadius: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
          }}
        >
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', textShadow: '1px 1px 0px #fff', fontSize: { xs: '1rem', md: '2.0rem' } }}>
            {i18next.t('top.site_description')}
          </Typography>
          <Typography variant="h5" gutterBottom sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', md: '1.1rem' }, whiteSpace: 'pre-line' }}>
            {i18next.t('top.site_notice')}
          </Typography>
        </Box>
      </Box>}

      {/* Announcements */}
      <Box sx={{ py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', md: '2.125rem' } }}>
              {i18next.t('nav.announcements')}
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton
                color="primary"
                onClick={() => navigate('/admin/announcements')}
                sx={{
                  bgcolor: '#f5f5f7',
                  '&:hover': { bgcolor: '#e8e8e8' },
                  borderRadius: '8px'
                }}
                title={i18next.t('store.view_all')}
              >
                <ReorderIcon />
              </IconButton>
              {user?.level === 'ADMIN' && (
                <IconButton
                  color="primary"
                  onClick={() => navigate('/admin/announcements/add')}
                  sx={{
                    bgcolor: '#f5f5f7',
                    '&:hover': { bgcolor: '#e8e8e8' },
                    borderRadius: '8px'
                  }}
                  title={i18next.t('common.add')}
                >
                  <AddIcon />
                </IconButton>
              )}
            </Stack>
          </Box>

          {/* 공지사항 게시판 형태 */}
          <Paper sx={{ p: 0, borderRadius: '12px', overflow: 'hidden' }}>
            {/* 공지사항 목록 */}
            {announcements?.map((announcement, index) => (
              <Box
                key={announcement.id}
                sx={{
                  p: 2,
                  borderBottom: '1px solid #f1f3f4',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    bgcolor: '#f8f9fa'
                  },
                  '&:last-child': {
                    borderBottom: 'none'
                  }
                }}
                onClick={() => navigate(`/admin/announcements/${announcement.id}`, { state: { from: '/' } })}
              >
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  {announcement.isImportant ?
                    <NotificationImportantIcon style={{ marginRight: 10 }} />
                    :
                    <CampaignIcon style={{ marginRight: 10 }} />
                  }
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: announcement.isImportant ? 600 : 400,
                      color: announcement.isImportant ? '#dc3545' : '#212529',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.75rem', md: '0.875rem' }
                    }}
                  >
                    {announcement.title}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    width: { xs: '80px', md: '100px' },
                    textAlign: 'center',
                    color: '#6c757d',
                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                  }}
                >
                  {formatDate(announcement.createdAt)}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
});

export default StoreMain;